import { useState, useEffect, useRef } from 'react';

const HISTORY_LEN = 30;
const POLL_MS = 1000;

function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Monitors Cesium 3D Tiles streaming quality by tracking pending requests,
 * tile load throughput, point counts, GPU memory, and failed tile loads.
 *
 * @param {object|null} viewer - Cesium Viewer instance
 */
export function useTileStreamQuality(viewer) {
  const [stats, setStats] = useState({
    pendingRequests: 0,
    tilesProcessing: 0,
    tilesLoaded: 0,
    tilesTotal: 0,
    tilesSelected: 0,
    pointsSelected: 0,
    pointsLoaded: 0,
    trianglesSelected: 0,
    geometryBytes: 0,
    textureBytes: 0,
    geometryLabel: '0 B',
    textureLabel: '0 B',
    failCount: 0,
  });
  const [history, setHistory] = useState(() => Array(HISTORY_LEN).fill(0));
  const failRef = useRef(0);
  const cleanupRef = useRef([]);

  // Listen for tile failures on all tilesets
  useEffect(() => {
    if (!viewer) return;
    failRef.current = 0;

    const attach = () => {
      cleanupRef.current.forEach(fn => fn());
      cleanupRef.current = [];

      if (viewer.isDestroyed?.()) return;
      const primitives = viewer.scene?.primitives;
      if (!primitives) return;

      for (let i = 0; i < primitives.length; i++) {
        const p = primitives.get(i);
        if (p?.tileFailed) {
          const handler = () => { failRef.current++; };
          p.tileFailed.addEventListener(handler);
          cleanupRef.current.push(() => p.tileFailed.removeEventListener(handler));
        }
      }
    };

    attach();

    const onAdd = () => attach();
    const primitiveAdded = viewer.scene?.primitives?.primitiveAdded;
    primitiveAdded?.addEventListener(onAdd);

    return () => {
      cleanupRef.current.forEach(fn => fn());
      cleanupRef.current = [];
      if (!viewer.isDestroyed?.()) {
        primitiveAdded?.removeEventListener(onAdd);
      }
    };
  }, [viewer]);

  // Poll all statistics
  useEffect(() => {
    if (!viewer) return;

    const timer = setInterval(() => {
      if (viewer.isDestroyed?.()) return;

      let pending = 0;
      let processing = 0;
      let loaded = 0;
      let total = 0;
      let selected = 0;
      let pointsSel = 0;
      let pointsLd = 0;
      let triSel = 0;
      let geomBytes = 0;
      let texBytes = 0;
      let batchBytes = 0;

      const primitives = viewer.scene?.primitives;
      if (primitives) {
        for (let i = 0; i < primitives.length; i++) {
          const p = primitives.get(i);
          if (p?.statistics) {
            const s = p.statistics;
            pending += (s.numberOfPendingRequests || 0);
            processing += (s.numberOfTilesProcessing || 0);
            loaded += (s.numberOfLoadedTilesTotal || 0);
            total += (s.numberOfTilesTotal || 0);
            selected += (s.selected || 0);
            pointsSel += (s.numberOfPointsSelected || 0);
            pointsLd += (s.numberOfPointsLoaded || 0);
            triSel += (s.numberOfTrianglesSelected || 0);
            geomBytes += (s.geometryByteLength || 0);
            texBytes += (s.texturesByteLength || 0);
            batchBytes += (s.batchTableByteLength || 0);
          }
        }
      }

      const totalBytes = geomBytes + texBytes + batchBytes;

      setStats({
        pendingRequests: pending,
        tilesProcessing: processing,
        tilesLoaded: loaded,
        tilesTotal: total,
        tilesSelected: selected,
        pointsSelected: pointsSel,
        pointsLoaded: pointsLd,
        trianglesSelected: triSel,
        geometryBytes: geomBytes,
        textureBytes: texBytes,
        batchTableBytes: batchBytes,
        totalBytes,
        geometryLabel: formatBytes(geomBytes),
        textureLabel: formatBytes(texBytes),
        batchTableLabel: formatBytes(batchBytes),
        totalLabel: formatBytes(totalBytes),
        failCount: failRef.current,
      });
      setHistory(prev => [...prev.slice(1), pending]);
    }, POLL_MS);

    return () => clearInterval(timer);
  }, [viewer]);

  // Determine quality level
  const { pendingRequests, failCount } = stats;
  let quality = null;
  let color = '#fff';

  if (viewer) {
    if (failCount > 5) {
      quality = 'poor';
      color = '#f44336';
    } else if (pendingRequests > 20) {
      quality = 'loading';
      color = '#ff9800';
    } else if (pendingRequests > 0) {
      quality = 'loading';
      color = '#fff';
    } else {
      quality = 'good';
      color = '#fff';
    }
  }

  return { quality, stats, history, color };
}
